import React, {useState, useEffect} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/userContext.js';
import BaseLayout from '../../components/BaseLayout.js';
import Card from '../../components/Card.js';
import Title from '../../components/Title.js';
import UploadButton from '../../components/UploadButton.js';
import ShortButton from '../../components/ShortButton.js';
import LoadingDots from '../../components/LoadingDots.js';
import Typography from '@mui/material/Typography';
import { motion, AnimatePresence } from 'framer-motion';


import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { getParsedResume } from '../../api/resume/parser.js';
import { CookingPot } from '../../components/CookingSoon.js';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// TODO: Add back in custom events for testing
const ResumeUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshSubscription } = useUser();

  const [file, setFile] = useState(null);
  const [jd, setJD] = useState('');
//   const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.2,
        duration: 0.6
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please upload a PDF file');
      setFile(null);
    }
  };

  const handleJDChange = (e) => {
    setJD(e.target.value);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
        setError('Please upload a file.');
        return;
    };

    if (!jd) {
        setError('Please enter a job description.');
        return;
    }

    setLoading(true);
    setError(null);

    try {
        window.dispatchEvent(new CustomEvent('pdf-parsing-started'));
        const arrayBuffer = await file.arrayBuffer();
        pdfjsLib.GlobalWorkerOptions.workerSrc = '//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

        const loadingTask = pdfjsLib.getDocument(arrayBuffer);
        const pdf = await loadingTask.promise;

        let fullText = '';
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            
            // Group items by line with section detection
            let currentLine = [];
            let lastY = null;
            let lastHeight = null;
            const sectionBreakThreshold = 5; // Adjust based on your PDF's spacing

            textContent.items.forEach((item, index) => {
                const y = item.transform[5];
                
                // Detect section breaks (larger vertical gaps)
                if (lastY !== null && (lastY - y - lastHeight) > sectionBreakThreshold) {
                    fullText += '\n\n';
                }
                
                // New line detection
                if (lastY !== null && Math.abs(y - lastY) > 4) {
                    fullText += '\n';
                    currentLine = [];
                }
                
                // Preserve intra-line spacing
                if (currentLine.length > 0 && item.str !== ' ') {
                    const lastItem = currentLine[currentLine.length - 1];
                    const xGap = item.transform[4] - (lastItem.transform[4] + lastItem.width);
                    
                    if (xGap > 3) { // Space between words
                        fullText += ' ';
                    }
                }
                
                fullText += item.str;
                currentLine.push(item);
                lastY = y;
                lastHeight = item.height;
            });
            
            fullText += '\n\n'; // Page separator
        }

        // Post-processing to match old parser format
        fullText = fullText
            .replace(/(\S)-\n(\S)/g, '$1$2') // Join hyphenated words
            .replace(/\n{3,}/g, '\n\n') // Normalize section breaks
            .replace(/\s{2,}/g, ' ');   // Clean extra spaces
        
    const parsedData = await getParsedResume(fullText);

    setResults(parsedData);
    window.dispatchEvent(new CustomEvent('pdf-parsing-completed'));
    navigate('/resume/submit', {state: {parsedData: parsedData, jobDescription: jd}});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
        <style>
        {`
            .center-container {
                display: flex;
                margin-top: 16%;
                transform: translateZ(0); /* GPU acceleration hint */
            }

            .grid-container {
                margin: 0 auto;
                padding: 0 12px;
                display: flex;
                flex-direction: column;
                position: relative;
                padding-top: 48px;
                will-change: transform, opacity; /* GPU acceleration hint */
            }

            .form-fields {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 16px;
                width: 100%;
            }

            .title-container {
                position: absolute;
                top: 0;
                left: 12px;
                text-align: left;
            }

            .title-gap {
                margin-bottom: 32px;
            }

            .textarea-container {
                width: 100%;
                margin: 4px 0;
            }

            .field-label {
                font-family: 'Poppins', sans-serif;
                font-size: 14px;
                color: #E1E1E6;
            }

            .field-input {
                padding: 12px;
                border-radius: 4px;
                border: 1px solid #6B3A8F;
                background: rgba(26, 26, 26, 0.5);
                color: #E1E1E6;
                font-family: 'Poppins', sans-serif;
                width: 100%;
            }

            textarea.field-input {
                min-height: 80px;
                resize: vertical;
            }

            .main-title {
                text-align: left;
                padding: 0 12px;
            }

            .error-message {
                padding-top: 16px;
                text-align: center;
                color: red;
            }

            .loading-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                padding: 24px;
                text-align: center;
            }

            p {
                font-family: 'Poppins', sans-serif;
                font-size: 14px;
                color: #E1E1E6;
                margin: 8px 0 0 0;
                opacity: 0.8;
            }
            
            .loading-message {
                display: flex;
                justify-content: center;
                align-items: center;
                margin-top: 24px;
                color: #9797FA;
            }
            
            .processing-indicator {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin-top: 24px;
            }
            
            .progress-dots {
                display: flex;
                margin-top: 8px;
            }
            
            .dot {
                width: 8px;
                height: 8px;
                background-color: #9797FA;
                border-radius: 50%;
                margin: 0 4px;
            }
            
            .dot:nth-child(1) {
                animation: pulse 1.5s infinite;
            }
            
            .dot:nth-child(2) {
                animation: pulse 1.5s infinite 0.3s;
            }
            
            .dot:nth-child(3) {
                animation: pulse 1.5s infinite 0.6s;
            }
            
            @keyframes pulse {
                0%, 100% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.3); opacity: 0.5; }
            }
        `}
        </style>

        <BaseLayout>
            {/* {initialLoading ? (
                <div className="loading-container">
                    <CookingPot />
                    <Typography variant="body2">Analysing your resume...</Typography>
                </div>            
            ) : ( */}
                <motion.div 
                    className="center-container"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <div className="grid-container">
                        <motion.div className="title-container" variants={itemVariants}>
                            <Title text="Resume Reviewer"/>
                        </motion.div>
                        <motion.div variants={fadeInVariants}>
                            <Card>
                                <div className="form-container">
                                    <motion.div className="title-gap" variants={itemVariants}>
                                        <Title text="Let's get you through"/>
                                    </motion.div>
                                    <form className="form-fields" onSubmit={handleSubmit}>
                                        <motion.div className="textarea-container" variants={itemVariants}>
                                            <label className="field-label">Job Description</label>
                                            <textarea 
                                                name="bio"
                                                className="field-input"
                                                placeholder="Enter your dream job scope here!"
                                                onChange={handleJDChange}
                                                data-cy="resumeUpload-textarea"
                                            />
                                        </motion.div>
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                            id="pdf-upload"
                                            data-cy="resumeUpload-input"
                                        />
                                        <motion.div variants={itemVariants}>
                                            <UploadButton
                                                onClick={() => document.getElementById('pdf-upload').click()}
                                                type="button"
                                            />
                                        </motion.div>
                                        
                                        <AnimatePresence>
                                            {file && (
                                                <motion.span
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    {file.name}
                                                </motion.span>
                                            )}
                                        </AnimatePresence>
                                        
                                        <motion.div variants={itemVariants}>
                                            <ShortButton text={loading ? <LoadingDots/> : "Begin!"} type="submit" disabled={loading} identifier='resumeBegin-button'/>
                                        </motion.div>
                                    </form>

                                    {/* Display error if any */}
                                    <AnimatePresence>
                                        {error && (
                                            <motion.div 
                                                className="error-message"
                                                initial={{ opacity: 0, y: 5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                Error: {error}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </Card>
                        </motion.div>
                    </div>
                </motion.div>
            {/* )} */}
        </BaseLayout>
    </>
  )
};

export default ResumeUpload;